// Artemis in Hunt Planner (guided intake): what the panel tells Artemis about the planner, and
// set_planner_filters, which fills in the planner's state, species and hunt type for the user.
// The option lists are state.huntplannerStatesData from HuntPlanner/GetStates:
//   [{ stateAbbr, stateName, species: [{ name, residency: [{ key, name }] }] }]

// Client actions in the planner. Map-changing ones are left out: they act on the map tab's map.
export const ARTEMIS_PLANNER_ACTIONS = ['ask_user', 'set_planner_filters']

const same = (a, b) => typeof a === 'string' && typeof b === 'string' && a.trim().toLowerCase() === b.trim().toLowerCase()
const huntTypeName = key => String(key || '').replace(/_/g, ' ')

// What the planner shows, for the request's Context: { Planner: { State, StateName, Species,
// HuntType, HuntTypeName, Unit, Weapon, Season, Sex } } without the empty ones (Weapon, Season and
// Sex are More filters' method, season and sex). { Planner: {} } while nothing's picked: it still
// tells Artemis the user is in Hunt Planner, so it opens by asking what they want to hunt.
export const plannerContext = (selected, statesData = [], moreFilters = null) => {
  const more = moreFilters && typeof moreFilters === 'object' && !Array.isArray(moreFilters) ? moreFilters : {}
  const filters = { Weapon: more.method || null, Season: more.season || null, Sex: more.sex || null }
  if (!selected || typeof selected !== 'object' || Array.isArray(selected)) return { Planner: {} }
  const picked = !!(selected.StateAbbreviation && selected.Species && selected.HuntTypeKey)
  const state = (statesData || []).find(s => s.stateAbbr === selected.StateAbbreviation)
  const species = state && (state.species || []).find(s => s.name === selected.Species)
  const huntType = species && (species.residency || []).find(r => r.key === selected.HuntTypeKey)
  const planner = {
    State: selected.StateAbbreviation || null,
    StateName: (state && state.stateName) || null,
    Species: selected.Species || null,
    HuntType: selected.HuntTypeKey || null,
    HuntTypeName: (huntType && huntType.name) || (selected.HuntTypeKey ? huntTypeName(selected.HuntTypeKey) : null),
    Unit: selected.unitName || null,
    // More filters belong to the picked hunt.
    ...(picked ? filters : {})
  }
  Object.keys(planner).forEach(k => planner[k] == null && delete planner[k])
  return { Planner: planner }
}

// The message Plan with Artemis opens with. Artemis answers it by asking what, where and with
// what weapon, then sets the planner's filters.
export const PLANNER_INTAKE_OPENER = 'Help me plan a hunt.'

// Matches set_planner_filters' { state, species, hunt_type } to the planner's options: a state by
// abbreviation or name, a species by name, a hunt type by key or name, ignoring case. Resolves to
//   { outcome: 'applied', selection: { StateAbbreviation, Species, HuntTypeKey }, draw, label }
// or { outcome: 'not_found', field, options } naming the first value that isn't offered and
// what is, so Artemis can ask again.
export const resolvePlannerFilters = (input, statesData = []) => {
  const want = input || {}
  const states = statesData || []
  const state = states.find(s => same(s.stateAbbr, want.state) || same(s.stateName, want.state))
  if (!state) return { outcome: 'not_found', field: 'state', options: states.map(s => s.stateAbbr) }
  const speciesList = state.species || []
  const species = speciesList.find(s => same(s.name, want.species))
  if (!species) return { outcome: 'not_found', field: 'species', options: speciesList.map(s => s.name) }
  const huntTypes = species.residency || []
  const huntType = huntTypes.find(r => same(r.key, want.hunt_type) || same(r.name, want.hunt_type))
  if (!huntType) return { outcome: 'not_found', field: 'hunt_type', options: huntTypes.map(r => r.name || r.key) }
  const draw = drawFilters(want)
  const filters = huntFilters(want)
  const built = [`${state.stateName || state.stateAbbr} · ${species.name} · ${huntType.name || huntTypeName(huntType.key)}`,
    filters.method, filters.season, filters.sex, drawLabel(draw)].filter(Boolean).join(' · ')
  return {
    outcome: 'applied',
    selection: { StateAbbreviation: state.stateAbbr, Species: species.name, HuntTypeKey: huntType.key },
    draw,
    filters,
    // The server's label when it sent one: it names what it checked and kept.
    label: (typeof want.label === 'string' && want.label.trim()) || built
  }
}

// More filters' { method, season, sex } from set_planner_filters' weapon, season and sex, with only
// what was given. The server has already matched them to the hunt's GetFilterValues lists.
export const huntFilters = input => {
  const out = {}
  const text = v => (typeof v === 'string' && v.trim() ? v.trim() : null)
  if (text(input && input.weapon)) out.method = text(input.weapon)
  if (text(input && input.season)) out.season = text(input.season)
  if (text(input && input.sex)) out.sex = text(input.sex)
  return out
}

const int = v => (typeof v === 'number' && Number.isInteger(v) ? v : typeof v === 'string' && /^\d+$/.test(v.trim()) ? Number(v) : null)

// The planner's draw-odds filters (More filters › Draw odds) from set_planner_filters' optional
// points, exact_points, min_draw_odds and draw_choice: { DrawPoints, ExactPoints, DrawPercentage,
// DrawChoice } with only what was given. Exact points needs at least one point, as in the menu.
export const drawFilters = input => {
  const out = {}
  const points = int(input && input.points)
  if (points != null && points >= 0 && points <= 50) out.DrawPoints = points
  if (input && input.exact_points === true && out.DrawPoints >= 1) out.ExactPoints = true
  const odds = int(input && input.min_draw_odds)
  if (odds != null && odds >= 0 && odds <= 100) out.DrawPercentage = odds
  const choice = input && typeof input.draw_choice === 'string' ? input.draw_choice.trim() : ''
  if (choice) out.DrawChoice = choice
  return out
}

const drawLabel = draw => {
  if (draw.DrawPoints == null) return draw.DrawPercentage ? `≥${draw.DrawPercentage}% odds` : ''
  const points = `${draw.DrawPoints} ${draw.DrawChoice === 'raffle' ? (draw.DrawPoints === 1 ? 'entry' : 'entries') : (draw.DrawPoints === 1 ? 'pt' : 'pts')}`
  return [draw.ExactPoints ? `exactly ${points}` : points, draw.DrawPercentage ? `≥${draw.DrawPercentage}% odds` : ''].filter(Boolean).join(', ')
}
